import httpService from './httpService';
import { ProfileField } from '../types/Profile';
import { PersonalConnection } from '../types/misc';
import {
  setPersonalConnections,
  setProfileFields,
  setProfilePicture,
} from '../redux/slices/userSlice';
import { store } from '../redux/store';
import { RootState } from '../redux/reducers';
import {
  SharedCommunityData,
  SharedCommunityDataResponse,
  UpdateCommunityConnectionOptions,
} from '../models/userService.model';

/**
 * The user service, contains all logic for user actions
 */
const userService = {
  getProfilePicture: async (): Promise<string> => {
    const { avatarChecked, profilePicture } = (store.getState() as RootState).user;
    // Skip the request when we already know there is no avatar (or one is loaded).
    if (avatarChecked) return profilePicture;

    return httpService
      .get('/users/@me/avatar')
      .then(({ data }): string => {
        store.dispatch(setProfilePicture(data.path));
        return data.path;
      })
      .catch(() => {
        // Mark as checked with an empty path so we stop retrying 404s.
        store.dispatch(setProfilePicture(''));
        return '';
      });
  },

  getConnections(): Promise<PersonalConnection[]> {
    return httpService.get<PersonalConnection[]>('/connections/@me/overview').then(({ data }) => {
      store.dispatch(setPersonalConnections(data));
      return data;
    });
  },

  getUserData(): Promise<ProfileField[]> {
    return httpService.get<ProfileField[]>('/properties/@me').then(({ data }): ProfileField[] => {
      store.dispatch(setProfileFields(data));
      return data;
    });
  },

  setFields(field: ProfileField[]): Promise<ProfileField[]> {
    return httpService
      .post<ProfileField[]>('/properties/@me', field)
      .then(({ data }): ProfileField[] => {
        const difference = data.filter(
          (nf) => !(store.getState() as RootState).user.fields?.map((f) => f.id).includes(nf.id),
        );
        store.dispatch(setProfileFields(data));
        return difference;
      });
  },

  updateCommunityConnection(
    communityId: number,
    args: SharedCommunityData,
    options: UpdateCommunityConnectionOptions = {},
  ): Promise<void> {
    const body = {
      ...args,
      ...options,
    };

    return httpService
      .put<void>(`/networks/communities/${communityId}/members/@me/shared-data`, body)
      .then();
  },

  async getSharedDataWithCommunity(communityId: number): Promise<SharedCommunityData> {
    const dashboardData = await httpService.get<SharedCommunityDataResponse>(
      `/networks/communities/${communityId}/members/@me/shared-data`,
    );

    const memberData = await httpService.get<{ propertyIds: number[] }>(
      `/networks/communities/${communityId}/members/@me/shared-data-with-members`,
    );

    return {
      propertyIds: dashboardData.data.fields.filter((f) => f.id).map((f) => f.id!),
      customFieldResponses: dashboardData.data.customFieldResponses,
      dataForMembers: memberData.data.propertyIds.map((pId: number): string => pId.toString()),
    };
  },
};

export default userService;
